import { IMapProps, IMapReciverProps, IMapSenderProps, ISolicitationMapLocationProps } from 'props';
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import { useCallback, useEffect, useRef, useState } from 'react';
import Modal from 'react-modal';
import { Typography } from '@mui/material';
import { ILocationViewModel } from 'viewModels';
import useWebSocket from '../../hooks/useWebSocket';
import { Container, Content } from './styles';
import { useAuth } from '../../context';
import { LocationMap } from '../../controller/LocationMapController';

export interface Position {
  lat: number;
  lng: number;
}

const Map = ({ destination, origin, destinationName, originName }: IMapProps) => {
  const [center, setCenter] = useState<google.maps.LatLng | undefined>(origin ?? undefined);
  const mapRef = useRef<google.maps.Map>();
  const originMarkerRef = useRef<google.maps.Marker>();
  const destinationMarkerRef = useRef<google.maps.Marker>();
  const directionsRendererRef = useRef<google.maps.DirectionsRenderer>();
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY ?? '',
  });

  const onLoad = useCallback(
    (map: google.maps.Map) => {
      mapRef.current = map;
      directionsRendererRef.current = new google.maps.DirectionsRenderer({
        suppressMarkers: true,
      });
      directionsRendererRef.current.setMap(map);
      destinationMarkerRef.current = new google.maps.Marker({
        position: destination,
        map,
        icon: {
          url: 'https://i.imgur.com/zzHvmxD.png',
          scaledSize: new google.maps.Size(50, 50),
        },
        label: {
          text: destinationName,
          className: 'titlePointMap',
        },
      });
      setCenter(origin ?? undefined);
      if (origin !== null && mapRef.current !== undefined) {
        originMarkerRef.current = new google.maps.Marker({
          position: origin,
          map: mapRef.current,
          icon: {
            url: 'https://i.imgur.com/zzHvmxD.png',
            scaledSize: new google.maps.Size(50, 50),
          },
          label: {
            text: originName,
            className: 'titlePointMap',
          },
        });
        const directionsServices = new google.maps.DirectionsService();
        directionsServices.route(
          {
            origin,
            destination,
            travelMode: google.maps.TravelMode.DRIVING,
          },
          (result) => {
            if (directionsRendererRef.current) {
              directionsRendererRef.current.setDirections(result);
            }
          },
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const onUnmount = useCallback(() => {
    mapRef.current = undefined;
  }, []);

  useEffect(() => {
    if (origin !== null && mapRef.current !== undefined) {
      originMarkerRef.current?.setPosition(origin);
      const directionsServices = new google.maps.DirectionsService();
      directionsServices.route(
        {
          origin,
          destination,
          travelMode: google.maps.TravelMode.DRIVING,
        },
        (result) => {
          if (directionsRendererRef.current) {
            directionsRendererRef.current.setDirections(result);
          }
        },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [origin]);

  return isLoaded ? (
    <GoogleMap
      options={{
        zoomControl: true,
        streetViewControl: false,
        mapTypeControl: false,
        fullscreenControl: false,
      }}
      mapContainerStyle={{
        width: '100%',
        height: '100%',
        borderRadius: '1rem',
      }}
      onLoad={onLoad}
      onUnmount={onUnmount}
      center={center}
      zoom={15}
    />
  ) : (
    <Typography variant="h5" component="p">
      carregando..
    </Typography>
  );
};

const Reciver = ({ destination, destinationName, originName }: IMapReciverProps) => {
  const { user } = useAuth();
  const [origin, setOrigin] = useState<google.maps.LatLng | null>(null);
  useWebSocket<ILocationViewModel>({
    url: `location-map/${user.id}/reciver`,
    onMessage: (response) => {
      setOrigin(
        new google.maps.LatLng(parseFloat(response.latitude), parseFloat(response.longitude)),
      );
    },
  });

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>
      {origin ? (
        <Map
          destination={destination}
          origin={origin}
          destinationName={destinationName}
          originName={originName}
        />
      ) : (
        <Typography variant="h3" component="p">
          Localização não encontrada
        </Typography>
      )}
    </>
  );
};

const Sender = ({ destination, solicitationId, destinationName, originName }: IMapSenderProps) => {
  const { user } = useAuth();
  const [origin, setOrigin] = useState<google.maps.LatLng | null>(null);

  const changePosition = ({ coords: { latitude, longitude } }: GeolocationPosition) => {
    setOrigin(new google.maps.LatLng(latitude, longitude));
    LocationMap.sendLocaion({
      id: solicitationId,
      latitude: latitude.toString(),
      longitude: longitude.toString(),
    });
  };

  useEffect(() => {
    const watcher = navigator.geolocation.watchPosition(changePosition, () => {});
  }, []);

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>
      {origin && (
        <Map
          destination={destination}
          origin={origin}
          destinationName={destinationName}
          originName={originName}
        />
      )}
    </>
  );
};

export const SolicitationMapLocation = ({
  solicitationId,
  address,
  isOpen,
  onClose,
  type,
  destinationName,
  originName,
}: ISolicitationMapLocationProps) => {
  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      overlayClassName="react-modal-overlay"
      style={{
        content: {
          background: 'none',
          width: '90%',
          maxWidth: '50rem',
          height: '65%',
          top: '8%',
          left: '1%',
          border: 'none',
        },
        overlay: {
          display: 'flex',
          alignContent: 'center',
          justifyContent: 'center',
        },
      }}>
      <Container>
        <Typography
          variant="h5"
          color="primary"
          component="p"
          alignSelf="flex-start"
          padding="1rem 0 0 1rem">
          Localização do {type === 'reciver' ? 'Correspondente' : 'Auditório'}
        </Typography>
        <Content>
          {address &&
            (type === 'reciver' ? (
              <Reciver
                destination={new google.maps.LatLng(address?.latitude ?? 0, address.longitude)}
                destinationName={destinationName}
                originName={originName}
              />
            ) : (
              <Sender
                destination={new google.maps.LatLng(address?.latitude ?? 0, address.longitude)}
                solicitationId={solicitationId}
                destinationName={destinationName}
                originName={originName}
              />
            ))}
        </Content>
      </Container>
    </Modal>
  );
};
